import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, CheckCircle2, Send, ClipboardList } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { toast } from "../hooks/use-toast";
import { STRATEGY_STEPS, CONTACT_FIELDS } from "../data/strategyFormSteps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAVY = "#1B2A4A";
const GOLD = "#C7A668";
const CREAM = "#F3EFE6";

const Field = ({ f, value, onChange }) => {
  const label = (
    <label className="block text-sm font-semibold mb-2" style={{ color: NAVY, fontFamily: "'Georgia', 'Forum', serif" }}>
      {f.label}
      {f.opt && <span className="ml-2 text-xs font-normal opacity-60">(необов&apos;язково)</span>}
      {f.required && !f.opt && <span className="ml-1 text-red-500">*</span>}
    </label>
  );

  const inputStyle = {
    background: "#fff",
    border: `1px solid rgba(27,42,74,0.15)`,
    color: NAVY,
    fontFamily: "'Arial', sans-serif"
  };

  if (f.type === "textarea") {
    return (
      <div className="mb-5">
        {label}
        <textarea
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(f.key, e.target.value)}
          className="w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/60 transition"
          style={inputStyle}
          data-testid={`sf-${f.key}`}
        />
      </div>
    );
  }

  if (f.type === "select") {
    return (
      <div className="mb-5">
        {label}
        <select
          value={value || ""}
          onChange={(e) => onChange(f.key, e.target.value)}
          className="w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/60 transition"
          style={inputStyle}
          data-testid={`sf-${f.key}`}
        >
          <option value="">— оберіть —</option>
          {f.options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>
    );
  }

  if (f.type === "multi-select") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      const nxt = arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt];
      onChange(f.key, nxt);
    };
    return (
      <div className="mb-5">
        {label}
        <div className="flex flex-wrap gap-2" data-testid={`sf-${f.key}`}>
          {f.options.map((opt) => {
            const active = arr.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className="px-4 py-2 rounded-full text-sm font-medium transition"
                style={{
                  background: active ? NAVY : "#fff",
                  color: active ? GOLD : NAVY,
                  border: `1px solid ${active ? NAVY : "rgba(27,42,74,0.2)"}`
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // default: text
  return (
    <div className="mb-5">
      {label}
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(f.key, e.target.value)}
        className="w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/60 transition"
        style={inputStyle}
        data-testid={`sf-${f.key}`}
      />
    </div>
  );
};

const buildMessage = (values) => {
  const lines = ["ЗАЯВКА НА СТРАТЕГІЮ ВСТУПУ", ""];
  STRATEGY_STEPS.forEach((step, i) => {
    lines.push(`━━ КРОК ${i + 1}: ${step.title} ━━`);
    step.fields.forEach((f) => {
      const v = values[f.key];
      if (v && String(v).trim() !== "" && !(Array.isArray(v) && v.length === 0)) {
        const val = Array.isArray(v) ? v.join(", ") : v;
        lines.push(`• ${f.label}: ${val}`);
      }
    });
    lines.push("");
  });
  return lines.join("\n");
};

const StrategyFormPage = () => {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  // Payment confirmation flow
  const [paidMode, setPaidMode] = useState(false); // toggle "already paid" input
  const [managerName, setManagerName] = useState("");
  const [paidConfirmed, setPaidConfirmed] = useState(false);
  const [paidBusy, setPaidBusy] = useState(false);

  const totalSteps = STRATEGY_STEPS.length;
  const isLast = step === totalSteps - 1;
  const isContact = step === totalSteps; // extra virtual step at end for contact info
  const currentDef = STRATEGY_STEPS[step];

  const progressPct = useMemo(() => {
    if (isContact) return 100;
    return ((step + 1) / (totalSteps + 1)) * 100;
  }, [step, totalSteps, isContact]);

  const setField = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const canGoNext = () => {
    if (isContact) return true;
    // First step name is required
    if (step === 0 && !values.f1_name) {
      toast({ title: "Заповніть ім'я та прізвище учня", variant: "destructive" });
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (!canGoNext()) return;
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrev = () => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!values.contact_email || !values.contact_phone) {
      toast({ title: "Заповніть e-mail і телефон для зв'язку", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, {
        name: values.f1_name || "Стратегія — без імені",
        phone: values.contact_phone,
        email: values.contact_email,
        country: (values.f40_countries || []).join(", "),
        program: "Замовити стратегію вступу",
        message: buildMessage(values),
        lang: "uk",
        source: "strategy-form"
      });
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast({ title: "Помилка відправки. Спробуйте ще раз.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const confirmPaidDirect = async () => {
    if (!managerName.trim()) {
      toast({ title: "Вкажіть ім'я менеджера STUDYGRI, якому було оплачено", variant: "destructive" });
      return;
    }
    setPaidBusy(true);
    try {
      await axios.post(`${API}/leads`, {
        name: values.f1_name || "Стратегія — без імені",
        phone: values.contact_phone || "",
        email: values.contact_email || "",
        country: (values.f40_countries || []).join(", "),
        program: "Стратегія — оплата напряму менеджеру",
        message: `✅ ОПЛАЧЕНО НАПРЯМУ · менеджер: ${managerName.trim()} · клієнт: ${values.f1_name || '—'} · email: ${values.contact_email || '—'} · телефон: ${values.contact_phone || '—'}`,
        lang: "uk",
        source: "strategy-paid-direct"
      });
      setPaidConfirmed(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast({ title: "Помилка. Спробуйте ще раз.", variant: "destructive" });
    } finally {
      setPaidBusy(false);
    }
  };

  if (sent) {
    // Final screen after "already paid to manager" is confirmed
    if (paidConfirmed) {
      return (
        <div className="min-h-screen flex flex-col" style={{ background: NAVY }}>
          <Header />
          <div className="flex-1 flex items-center justify-center px-5 py-20">
            <div className="max-w-xl w-full text-center rounded-3xl p-10 md:p-14" style={{ background: CREAM, boxShadow: `0 30px 60px -20px rgba(0,0,0,0.5)` }} data-testid="strategy-paid-confirmed">
              <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6" style={{ background: GOLD }}>
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h1 className="font-display text-3xl md:text-4xl uppercase mb-3" style={{ color: NAVY, fontFamily: "'Georgia', 'Forum', serif", fontWeight: 900 }}>Дякуємо!</h1>
              <p className="text-sm md:text-base leading-relaxed" style={{ color: "#444" }}>
                Ми звіримо оплату з менеджером <strong style={{ color: NAVY }}>{managerName}</strong> та надішлемо готову стратегію у <strong>Telegram / WhatsApp</strong> протягом 7 робочих днів.
              </p>
              <Link to="/" className="inline-block mt-8 px-8 py-3 rounded-full font-semibold transition hover:opacity-90" style={{ background: NAVY, color: GOLD }}>
                На головну
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex flex-col" style={{ background: NAVY }}>
        <Header />
        <div className="flex-1 flex items-center justify-center px-5 py-20">
          <div className="max-w-xl w-full text-center rounded-3xl p-8 md:p-12" style={{ background: CREAM, boxShadow: `0 30px 60px -20px rgba(0,0,0,0.5)` }} data-testid="strategy-form-success">
            <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6" style={{ background: GOLD }}>
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-display text-3xl md:text-4xl uppercase mb-3" style={{ color: NAVY, fontFamily: "'Georgia', 'Forum', serif", fontWeight: 900 }}>Дякуємо!</h1>
            <p className="text-sm md:text-base leading-relaxed mb-7" style={{ color: "#444", fontFamily: "'Arial', sans-serif" }}>
              Ваша анкета збережена. Останній крок — оплата <strong style={{ color: NAVY }}>€290</strong> за розробку стратегії.
            </p>

            {/* OPTION 1 — Stripe */}
            <div className="rounded-2xl p-5 mb-4 text-left" style={{ background: '#fff', border: `2px solid ${GOLD}` }}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase" style={{ color: GOLD }}>ВАРІАНТ 1</div>
                <div className="font-display text-2xl" style={{ color: NAVY, fontWeight: 900 }}>€290</div>
              </div>
              <div className="text-sm mb-3" style={{ color: '#444' }}>Оплатити зараз онлайн — картка / Apple Pay / Google Pay через Stripe.</div>
              <a
                href="https://buy.stripe.com/bJe8wP4Xlfpged04CU8g01q"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="strategy-stripe-pay-btn"
                className="inline-flex items-center justify-center gap-2 w-full h-12 rounded-full font-bold text-sm transition hover:opacity-90"
                style={{ background: NAVY, color: GOLD }}
              >
                💳 Оплатити €290 через Stripe
              </a>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px" style={{ background: '#D8CDB8' }} />
              <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#888' }}>або</div>
              <div className="flex-1 h-px" style={{ background: '#D8CDB8' }} />
            </div>

            {/* OPTION 2 — Already paid to manager */}
            <div className="rounded-2xl p-5 text-left" style={{ background: '#fff', border: '2px solid #D8CDB8' }}>
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase mb-2" style={{ color: '#888' }}>ВАРІАНТ 2</div>
              <div className="font-display text-base font-bold mb-2" style={{ color: NAVY }}>Вже оплатив(ла) менеджеру STUDYGRI</div>
              <div className="text-sm mb-3" style={{ color: '#444' }}>Якщо ви вже перерахували оплату напряму менеджеру — вкажіть його ім'я, і ми звіримо оплату.</div>

              {!paidMode ? (
                <button
                  onClick={() => setPaidMode(true)}
                  data-testid="strategy-paid-toggle"
                  className="w-full h-11 rounded-full font-semibold text-sm transition hover:opacity-90"
                  style={{ background: '#fff', color: NAVY, border: `2px solid ${NAVY}` }}
                >
                  Я вже оплатив(ла) менеджеру →
                </button>
              ) : (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={managerName}
                    onChange={(e) => setManagerName(e.target.value)}
                    placeholder="Ім'я менеджера STUDYGRI"
                    data-testid="strategy-manager-name"
                    className="w-full h-11 px-4 rounded-full text-sm outline-none"
                    style={{ background: '#FAF8F5', border: '1.5px solid #D8CDB8', color: NAVY }}
                  />
                  <button
                    onClick={confirmPaidDirect}
                    disabled={paidBusy}
                    data-testid="strategy-paid-confirm-btn"
                    className="w-full h-11 rounded-full font-bold text-sm transition hover:opacity-90 disabled:opacity-60"
                    style={{ background: NAVY, color: GOLD }}
                  >
                    {paidBusy ? 'Відправляємо…' : 'Підтвердити відправку анкети'}
                  </button>
                </div>
              )}
            </div>

            <p className="text-[11px] mt-6" style={{ color: '#888' }}>Стратегія буде надіслана в Telegram / WhatsApp протягом 7 робочих днів після підтвердження оплати.</p>

            <Link to="/" className="inline-block mt-4 text-xs font-semibold transition hover:opacity-80" style={{ color: NAVY }}>
              ← На головну
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: NAVY }}>
      <Header />

      {/* Hero band */}
      <section className="pt-28 pb-8 md:pt-36 md:pb-12 relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full" style={{ background: `radial-gradient(circle, rgba(199,166,104,0.22) 0%, transparent 70%)` }} />
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center relative">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-bold tracking-[0.22em] uppercase mb-5" style={{ background: "rgba(199,166,104,0.14)", border: `1px solid ${GOLD}55`, color: GOLD }}>
            <ClipboardList className="w-3 h-3" /> АНКЕТА · 8 КРОКІВ · €290
          </div>
          <h1 className="font-display text-3xl md:text-5xl text-balance leading-[1.05] mb-4" style={{ color: "#fff", fontFamily: "'Georgia', 'Forum', serif", fontWeight: 900, letterSpacing: "-0.02em" }}>
            Замовити стратегію вступу
          </h1>
          <p className="text-base md:text-lg" style={{ color: "rgba(255,255,255,0.7)", fontFamily: "'Arial', sans-serif" }}>
            Заповніть анкету, оплатіть <strong style={{ color: GOLD }}>€290</strong> — і протягом тижня отримаєте персональну стратегію вступу в <strong style={{ color: GOLD }}>Telegram або WhatsApp</strong> від команди STUDYGRI.
          </p>
        </div>
      </section>

      {/* Form card */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          {/* Progress */}
          <div className="mb-6" data-testid="strategy-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: GOLD }}>
                {isContact ? "Крок 9 · Контакти" : `Крок ${step + 1} з ${totalSteps}`}
              </span>
              <span className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>{Math.round(progressPct)}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progressPct}%`, background: `linear-gradient(90deg, ${GOLD} 0%, #E1C68B 100%)` }} />
            </div>
          </div>

          {/* Card */}
          <div className="rounded-3xl p-6 md:p-10" style={{ background: CREAM, boxShadow: `0 30px 60px -25px rgba(0,0,0,0.55)` }}>
            {!isContact ? (
              <>
                <h2 className="font-display text-2xl md:text-3xl uppercase mb-1.5" style={{ color: NAVY, fontFamily: "'Georgia', 'Forum', serif", fontWeight: 900 }} data-testid="strategy-step-title">
                  {currentDef.title}
                </h2>
                <p className="text-sm md:text-base mb-8" style={{ color: "#555", fontFamily: "'Arial', sans-serif" }}>
                  {currentDef.subtitle}
                </p>
                <div>
                  {currentDef.fields.map((f) => (
                    <Field key={f.key} f={f} value={values[f.key]} onChange={setField} />
                  ))}
                </div>
              </>
            ) : (
              <>
                <h2 className="font-display text-2xl md:text-3xl uppercase mb-1.5" style={{ color: NAVY, fontFamily: "'Georgia', 'Forum', serif", fontWeight: 900 }}>
                  Контактна інформація
                </h2>
                <p className="text-sm md:text-base mb-8" style={{ color: "#555", fontFamily: "'Arial', sans-serif" }}>
                  Куди надіслати вашу стратегію та як з вами зв&apos;язатися.
                </p>
                <div>
                  {CONTACT_FIELDS.map((f) => (
                    <Field key={f.key} f={f} value={values[f.key]} onChange={setField} />
                  ))}
                </div>
              </>
            )}

            {/* Nav buttons */}
            <div className="mt-8 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <button
                type="button"
                onClick={goPrev}
                disabled={step === 0}
                className="inline-flex items-center justify-center gap-2 px-6 h-12 rounded-full font-semibold transition disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ background: "transparent", color: NAVY, border: `1px solid ${NAVY}` }}
                data-testid="strategy-btn-prev"
              >
                <ChevronLeft className="w-4 h-4" /> Назад
              </button>

              {!isContact ? (
                !isLast ? (
                  <button
                    type="button"
                    onClick={goNext}
                    className="inline-flex items-center justify-center gap-2 px-8 h-12 rounded-full font-semibold transition hover:opacity-90"
                    style={{ background: NAVY, color: GOLD }}
                    data-testid="strategy-btn-next"
                  >
                    Далі <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={goNext}
                    className="inline-flex items-center justify-center gap-2 px-8 h-12 rounded-full font-semibold transition hover:opacity-90"
                    style={{ background: NAVY, color: GOLD }}
                    data-testid="strategy-btn-to-contact"
                  >
                    До контактів <ChevronRight className="w-4 h-4" />
                  </button>
                )
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy}
                  className="inline-flex items-center justify-center gap-2 px-8 h-12 rounded-full font-semibold transition hover:opacity-90 disabled:opacity-60"
                  style={{ background: GOLD, color: NAVY }}
                  data-testid="strategy-btn-submit"
                >
                  {busy ? "Відправляємо..." : (<>Надіслати стратегію <Send className="w-4 h-4" /></>)}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StrategyFormPage;
